import { el, clear } from "../components/dom.js";
import { searchBar } from "../components/search-bar.js";
import { expandableResultCard } from "../components/search-result.js";
import { discoverTabs } from "../components/discover-tabs.js";
import { searchGames, searchGameSeries } from "../api/igdb.js";
import { addGame, deleteGameByIgdbId, listGames } from "../db/games.js";
import { navigate, navigateBack } from "../main.js";
import { magScreenHead, MAG } from "../components/magazine/kit.js";
import { fannedSpines } from "../components/magazine/shelf.js";

export async function renderAddGame(root) {
	const head = magScreenHead("Add a Game", {
		eyebrow: "Find & collect",
		eyebrowColor: "#19d3e8",
		onBack: () => navigateBack("/games"),
	});

	const resultsContainer = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", padding: "6px 0 24px" } });
	const statusLine = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px",
			letterSpacing: ".14em", textTransform: "uppercase", color: MAG.lime, padding: "10px 0 4px",
		},
	});

	// Pre-load existing IGDB ids so already-added rows render correctly.
	const existing = await listGames();
	const addedIds = new Set(existing.map((g) => g.igdb_id));

	let inflight = null;
	let lastResults = [];
	let lastSeries = []; // matching series, surfaced at the top of a normal search
	let mode = "games"; // "games" | "series"

	function renderResults() {
		clear(resultsContainer);
		if (mode === "series") {
			for (const s of lastResults) resultsContainer.append(seriesCard(s));
			return;
		}
		// Matching series lead a normal search so "zelda" surfaces the whole series
		// first, clearly marked — tapping it opens the series (not added by appearing).
		for (const s of lastSeries) resultsContainer.append(seriesCard(s));
		for (const result of lastResults) {
			resultsContainer.append(
				expandableResultCard({
					cover_url: result.cover_url,
					mediaType: "videogame",
					title: result.name,
					subtitle: result.release_year ? String(result.release_year) : "",
					summary: result.summary ?? "",
					details: [
						{ label: "Platforms", value: (result.platforms ?? []).join(", ") },
						{ label: "Genres", value: (result.genres ?? []).join(", ") },
					],
					added: addedIds.has(result.igdb_id),
					onToggleAdd: (want) => toggle(result, want),
				}),
			);
		}
	}

	// A game-series (IGDB collection) result: fanned spines + Anton name + N games,
	// opening the episode-style series view.
	function seriesCard(s) {
		const fakeMembers = Array.from({ length: Math.min(3, Math.max(1, s.game_count || 1)) });
		return el("div", {
			role: "button",
			onclick: () => navigate(`/games/series/${s.collection_id}`),
			style: {
				display: "flex", gap: "12px", alignItems: "center", padding: "12px", cursor: "pointer",
				borderRadius: "15px", background: "rgba(255,255,255,.06)", border: `1px solid ${MAG.line}`,
				backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)",
			},
		},
			fannedSpines(fakeMembers, { height: "56px", width: "26px" }),
			el("div", { style: { flex: "1", minWidth: "0" } },
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
						letterSpacing: ".12em", textTransform: "uppercase", color: "#0b0b0f",
						background: MAG.lime, display: "inline-block", padding: "2px 6px",
						transform: "skewX(-7deg)", marginBottom: "5px",
					},
				}, "◆ Series"),
				el("div", {
					style: { fontFamily: "'Anton', sans-serif", fontSize: "20px", lineHeight: ".95", textTransform: "uppercase", color: "#fff" },
				}, s.title),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px",
						letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px",
					},
				}, s.game_count ? `${s.game_count} games` : "Game series"),
			),
			el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "22px", color: MAG.lime } }, "›"),
		);
	}

	// Returns the resulting added-state so the card can repaint in place.
	async function toggle(result, want) {
		try {
			if (!want) {
				await deleteGameByIgdbId(result.igdb_id);
				addedIds.delete(result.igdb_id);
				return false;
			}
			await addGame({
				igdb_id: result.igdb_id,
				title: result.name,
				release_year: result.release_year,
				release_date: result.release_date ?? null,
				release_precision: result.release_precision ?? null,
				cover_url: result.cover_url,
				platforms: result.platforms ?? [],
				genres: result.genres ?? [],
				summary: result.summary,
				franchise: result.franchise ?? null,
				collection: result.collection ?? null,
			});
			addedIds.add(result.igdb_id);
			return true;
		} catch (err) {
			statusLine.textContent = `Could not ${want ? "add" : "remove"}: ${err.message}`;
			throw err;
		}
	}

	async function runSearch(q) {
		if (inflight) inflight.abort();
		lastResults = [];
		lastSeries = [];
		clear(resultsContainer);
		statusLine.textContent = "";
		if (!q) return;

		const controller = new AbortController();
		inflight = controller;
		statusLine.textContent = "Searching…";

		// Series-only mode: just the series cards.
		if (mode === "series") {
			try {
				const results = await searchGameSeries(q, { signal: controller.signal });
				if (controller.signal.aborted) return;
				lastResults = results;
				statusLine.textContent = results.length ? `${results.length} series` : "No results.";
				renderResults();
			} catch (err) {
				if (err.name !== "AbortError") statusLine.textContent = `Error: ${err.message}`;
			} finally {
				if (inflight === controller) inflight = null;
			}
			return;
		}

		// Normal game search: also look up series (best-effort) and surface the top
		// match(es) above the game results.
		const seriesP = searchGameSeries(q, { signal: controller.signal }).catch(() => []);

		try {
			const results = await searchGames(q, { signal: controller.signal });
			if (controller.signal.aborted) return;
			lastResults = results;
			statusLine.textContent = results.length
				? `${results.length} result${results.length === 1 ? "" : "s"}`
				: "No results.";
			renderResults();
		} catch (err) {
			if (err.name === "AbortError") return;
			statusLine.textContent = `Error: ${err.message}`;
		} finally {
			if (inflight === controller) inflight = null;
		}

		const series = await seriesP;
		if (!controller.signal.aborted && Array.isArray(series) && series.length) {
			lastSeries = series.slice(0, 2);
			renderResults();
		}
	}

	const bar = searchBar({
		placeholder: "Search games…",
		onInput: (q) => runSearch(q),
	});

	// Mode pills: regular game search vs series (IGDB collection) search.
	const MODES = [
		{ value: "games", label: "Games", placeholder: "Search games…" },
		{ value: "series", label: "Series", placeholder: "Find a series — e.g. Zelda, Final Fantasy…" },
	];
	const modeChips = el("div", { style: { display: "flex", gap: "7px", padding: "12px 0 0" } });
	function paintModes() {
		for (const c of modeChips.children) {
			const on = c.dataset.value === mode;
			Object.assign(c.style, {
				color: on ? "#0b0b0f" : MAG.dim, background: on ? MAG.lime : "rgba(255,255,255,.07)",
				border: on ? "0" : `1px solid ${MAG.line}`, fontWeight: on ? "900" : "800",
			});
		}
	}
	for (const m of MODES) {
		const chip = el("button", {
			type: "button",
			dataset: { value: m.value },
			onclick: () => {
				if (mode === m.value) return;
				mode = m.value;
				bar.input.placeholder = m.placeholder;
				paintModes();
				runSearch(bar.input.value.trim());
			},
			style: {
				fontFamily: "'Archivo', sans-serif", fontSize: "10px", letterSpacing: ".05em",
				textTransform: "uppercase", borderRadius: "999px", padding: "7px 13px", cursor: "pointer",
			},
		}, m.label);
		modeChips.append(chip);
	}
	paintModes();

	root.append(
		el(
			"div.view",
			{},
			head,
			discoverTabs("search"),
			el("div", { style: { padding: "14px 0 0" } }, bar.node),
			modeChips,
			statusLine,
			resultsContainer,
		),
	);
}
